import type { WidgetPublicConfig } from '@studiof-agents/shared';

/**
 * URL de base de l'API widget. Injectée au build via process.env.VITE_API_BASE_URL
 * ou par défaut détectée depuis le src du <script data-agent>.
 */
let API_BASE: string | null = null;

export function setApiBase(url: string): void {
  API_BASE = url.replace(/\/$/, '');
}

export function getApiBase(): string {
  if (!API_BASE) {
    throw new Error('[StudioFAgents] API base URL not configured');
  }
  return API_BASE;
}

/**
 * Mode de fonctionnement du widget.
 *   - prod : embed chez un client. Identification par slug client, routes
 *     /api/widget/config/[slug] et /api/widget/chat (allowed_domains enforced).
 *   - demo : page publique /demo/[agentId] sur notre domaine. Identification
 *     par agent UUID, routes /api/widget/demo-config/[id] et
 *     /api/widget/demo-chat. Pas de check allowed_domains, plafond + rate
 *     limit serrés côté serveur.
 *   - preview : mode admin uniquement utilise dans l'iframe live preview du
 *     formulaire branding. Fetch la config via /api/widget/preview-config qui
 *     accepte des overrides en query string (design / layout / couleurs / etc.).
 *     Le chat est bloque (le serveur renvoie 403 pour /api/widget/demo-chat
 *     sur agentId non live, et de toute facon on ne veut pas consommer du LLM
 *     juste pour previewer un visuel). searchString = window.location.search
 *     transmis tel quel pour que le serveur applique les overrides.
 *
 * Une seule variante par instance, main.tsx valide a la lecture des data
 * attributes.
 */
export type WidgetMode =
  | { kind: 'prod'; slug: string }
  | { kind: 'demo'; agentId: string }
  | { kind: 'preview'; agentId: string; searchString: string };

/**
 * Identifiant stable pour la session storage (localStorage key + UI bindings).
 * Slug en prod, agentId en démo. Garantit que deux instances (embed prod + démo
 * sur le même browser) ne se cannibalisent pas leurs sessionStorage.
 */
export function modeIdentity(mode: WidgetMode): string {
  if (mode.kind === 'prod') return mode.slug;
  if (mode.kind === 'demo') return `demo-${mode.agentId}`;
  return `preview-${mode.agentId}`;
}

function configUrl(mode: WidgetMode): string {
  const base = getApiBase();
  if (mode.kind === 'prod') {
    return `${base}/api/widget/config/${encodeURIComponent(mode.slug)}`;
  }
  if (mode.kind === 'demo') {
    return `${base}/api/widget/demo-config/${encodeURIComponent(mode.agentId)}`;
  }
  // preview : on ajoute la searchString telle quelle pour transmettre les
  // overrides (design, layout, couleurs) au serveur qui les merge avec la
  // config DB de l'agent.
  return `${base}/api/widget/preview-config/${encodeURIComponent(mode.agentId)}${mode.searchString}`;
}

function chatUrl(mode: WidgetMode): string {
  const base = getApiBase();
  if (mode.kind === 'prod') return `${base}/api/widget/chat`;
  // demo + preview utilisent demo-chat. Preview ne devrait techniquement pas
  // appeler le chat (le LLM coute pour rien), mais si jamais le user clique
  // sur Envoyer, on tombe sur demo-chat qui renverra une erreur explicite.
  return `${base}/api/widget/demo-chat`;
}

export async function fetchConfig(mode: WidgetMode): Promise<WidgetPublicConfig> {
  const response = await fetch(configUrl(mode), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'omit',
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Config fetch failed (${response.status}): ${detail}`);
  }

  return (await response.json()) as WidgetPublicConfig;
}

export type ChatEvent =
  | { type: 'meta'; conversation_id: string; token: string }
  | { type: 'token'; delta: string }
  | { type: 'done'; tokens_in: number; tokens_out: number }
  | { type: 'error'; message: string };

export type HistoryMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
};

export type HistoryResponse = {
  conversation_id: string;
  messages: HistoryMessage[];
};

/**
 * Réhydrate l'historique d'une conversation pour le widget. Le token est passé
 * dans l'en-tête Authorization Bearer, jamais dans la query string : c'est une
 * crédential, OWASP standard 8.3 (pas de secret dans une URL).
 *
 * Retourne null si le token est refusé (403), la conv n'existe pas (404) ou
 * tout autre échec : le widget reprend alors avec un état vierge. On NE jette
 * PAS d'exception pour ne pas bloquer le démarrage du widget si le serveur
 * est temporairement HS.
 */
export async function fetchHistory(
  conversationId: string,
  token: string,
): Promise<HistoryResponse | null> {
  try {
    const response = await fetch(
      `${getApiBase()}/api/widget/conversations/${encodeURIComponent(conversationId)}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'omit',
      },
    );
    if (!response.ok) return null;
    return (await response.json()) as HistoryResponse;
  } catch {
    return null;
  }
}

export type StreamPayload = {
  session_id: string;
  message: string;
  visitor_email?: string;
  /** Honeypot — uniquement pertinent en mode prod. Ignoré en démo. */
  website?: string;
};

/**
 * POST vers la bonne route chat selon le mode, consommation SSE identique.
 * Le body est composé ici (ajout de agent_slug ou agent_id selon le mode).
 */
export async function* streamChat(
  mode: WidgetMode,
  payload: StreamPayload,
): AsyncGenerator<ChatEvent> {
  const body =
    mode.kind === 'prod'
      ? {
          agent_slug: mode.slug,
          session_id: payload.session_id,
          message: payload.message,
          visitor_email: payload.visitor_email,
          website: payload.website,
        }
      : {
          // demo et preview partagent le format demo-chat (agent_id en UUID).
          agent_id: mode.agentId,
          session_id: payload.session_id,
          message: payload.message,
          visitor_email: payload.visitor_email,
        };

  const response = await fetch(chatUrl(mode), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    credentials: 'omit',
  });

  if (!response.ok) {
    // Tente de parser un body JSON {error: "..."} (cas 429, 403, etc.)
    let friendly: string | null = null;
    try {
      const errBody = await response.clone().json();
      if (errBody && typeof errBody.error === 'string') {
        friendly = errBody.error;
      }
    } catch {
      // pas du JSON, on tombera sur le fallback
    }
    if (response.status === 429) {
      yield {
        type: 'error',
        message:
          friendly ??
          (mode.kind === 'prod' && mode.slug === 'holiclay'
            ? 'Trop de messages, veuillez réessayer plus tard.'
            : 'Trop de messages, réessaie plus tard.'),
      };
      return;
    }
    const detail = friendly ?? (await response.text().catch(() => ''));
    yield { type: 'error', message: `Chat failed (${response.status}): ${detail}` };
    return;
  }

  if (!response.body) {
    yield { type: 'error', message: 'No response body' };
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Découpe par double saut de ligne (séparateur SSE)
      let separator = buffer.indexOf('\n\n');
      while (separator !== -1) {
        const rawEvent = buffer.slice(0, separator);
        buffer = buffer.slice(separator + 2);

        const dataLine = rawEvent
          .split('\n')
          .find((line) => line.startsWith('data: '))
          ?.slice(6);

        if (dataLine) {
          try {
            const parsed = JSON.parse(dataLine) as ChatEvent;
            yield parsed;
          } catch {
            // ignore malformed lines
          }
        }

        separator = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
  }
}
